import { Outlet } from '@tanstack/react-router';
import type { ProjectId, SquadId } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { useKanbanStream } from '@web/ui/board/hooks/use_kanban_stream';
import {
  AppIcons,
  GroupedRail,
  IconButton,
  NavigationRail,
  PopoverMenu,
  ProfileMenu,
  Sidebar,
  SidebarDivider,
  type SidebarIconRailItem,
  useOrgContext,
  useProfileMenu,
} from '@web/ui/core';
import { useCommandPalette, useSidebarCollapsed } from '@web/data/repositories';
import { useOrgSidebar, type OrgSidebarProject, type OrgSidebarState } from '@web/ui/home/hooks';
import { CommandPalette, SidebarSearchButton, useCommandPaletteShortcut } from '@web/ui/search';

/**
 * Icon-only rail shown when the sidebar is collapsed: fixed nav as the first
 * group, each project as an icon in the second. Projects re-expand the rail
 * (their squads are the real boards and only live in the full tree).
 */
function buildCollapsedItems(
  sidebar: OrgSidebarState,
  setPaletteOpen: (open: boolean) => void,
  setCollapsed: (collapsed: boolean) => void,
): SidebarIconRailItem[][] {
  return [
    [
      {
        icon: 'magnifying-glass',
        label: t('search.openLabel'),
        onClick: () => {
          setPaletteOpen(true);
        },
      },
      {
        icon: 'house',
        label: t('home.nav.process'),
        active: sidebar.isOverviewActive,
        onClick: sidebar.goOverview,
      },
      {
        icon: 'tray',
        label: t('home.nav.triage'),
        active: sidebar.isTriageActive,
        onClick: sidebar.goTriage,
      },
      ...(sidebar.alertsEnabled
        ? [
            {
              icon: 'warning-octagon' as const,
              label: t('home.nav.alerts'),
              active: sidebar.isAlertsActive,
              onClick: sidebar.goAlerts,
            },
          ]
        : []),
      ...(sidebar.canSeeMice
        ? [
            {
              icon: 'briefcase' as const,
              label: t('home.nav.mice'),
              active: sidebar.isMiceActive,
              onClick: sidebar.goMice,
            },
          ]
        : []),
      ...(sidebar.canViewAs
        ? [
            {
              icon: 'device-mobile' as const,
              label: t('nav.viewAs'),
              onClick: sidebar.goViewAs,
            },
          ]
        : []),
    ],
    sidebar.projects.map((p) => ({
      icon: p.icon,
      label: p.name,
      active: p.isActive,
      onClick: () => {
        setCollapsed(false);
      },
    })),
  ];
}

/**
 * Org workspace layout — the chrome rendered around every page below
 * `/orgs/$orgSlug`. Owns the sidebar (nav + projects tree + profile
 * foot) and yields the main pane to the active route's `<Outlet/>`.
 * All data shaping and navigation lives in `useOrgSidebar`; this
 * file only positions things on screen.
 */
export default function OrgWorkspaceLayout(): React.JSX.Element {
  const { orgId } = useOrgContext();
  // Session-level: keep the org's board caches live for the whole workspace.
  useKanbanStream(orgId);
  const { user, signOut } = useProfileMenu();
  const sidebar = useOrgSidebar();
  const setPaletteOpen = useCommandPalette((s) => s.setOpen);
  const setCollapsed = useSidebarCollapsed((s) => s.setCollapsed);
  useCommandPaletteShortcut();

  const collapsedItems = buildCollapsedItems(sidebar, setPaletteOpen, setCollapsed);

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        brandLabel={t('home.sidebar.brand.os')}
        collapsedItems={collapsedItems}
        foot={<ProfileMenu user={user} onSignOut={signOut} />}
        body={
          <>
            <div className="px-2">
              <SidebarSearchButton />
            </div>

            <SidebarDivider />

            <NavigationRail>
              <NavigationRail.Item
                icon="house"
                label={t('home.nav.process')}
                active={sidebar.isOverviewActive}
                count={sidebar.overviewCount}
                onClick={sidebar.goOverview}
              />
              <NavigationRail.Item
                icon="tray"
                label={t('home.nav.triage')}
                active={sidebar.isTriageActive}
                count={sidebar.triageCount}
                onClick={sidebar.goTriage}
              />
              {sidebar.alertsEnabled ? (
                <NavigationRail.Item
                  icon="warning-octagon"
                  label={t('home.nav.alerts')}
                  active={sidebar.isAlertsActive}
                  count={sidebar.alertsCount}
                  onClick={sidebar.goAlerts}
                />
              ) : null}
              {sidebar.canSeeMice ? (
                <NavigationRail.Item
                  icon="briefcase"
                  label={t('home.nav.mice')}
                  active={sidebar.isMiceActive}
                  onClick={sidebar.goMice}
                />
              ) : null}
              {sidebar.canViewAs ? (
                <NavigationRail.Item
                  icon="device-mobile"
                  label={t('nav.viewAs')}
                  onClick={sidebar.goViewAs}
                />
              ) : null}
            </NavigationRail>

            <SidebarDivider />

            <GroupedRail
              title={t('home.processSidebar.projects')}
              trailing={
                sidebar.canCreateProject ? (
                  <IconButton
                    icon="plus"
                    size="xs"
                    variant="ghost"
                    title={t('home.processSidebar.newProject')}
                    aria-label={t('home.processSidebar.newProject')}
                    onClick={sidebar.goNewProject}
                  />
                ) : undefined
              }
            >
              {sidebar.projects.map((p) => (
                <ProjectGroup
                  key={p.id}
                  project={p}
                  onEdit={sidebar.goEditProject}
                  onCreateSquad={sidebar.goCreateSquad}
                  onSelectSquad={sidebar.goSquad}
                  onEditSquad={sidebar.goEditSquad}
                />
              ))}
            </GroupedRail>
          </>
        }
      />
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
      <CommandPalette />
    </div>
  );
}

type ProjectGroupProps = {
  project: OrgSidebarProject;
  onEdit: (id: ProjectId) => void;
  onCreateSquad: (id: ProjectId) => void;
  onSelectSquad: (id: SquadId) => void;
  onEditSquad: (id: SquadId) => void;
};

function ProjectGroup({
  project,
  onEdit,
  onCreateSquad,
  onSelectSquad,
  onEditSquad,
}: Readonly<ProjectGroupProps>): React.JSX.Element {
  const trailing = project.canEdit ? (
    <PopoverMenu
      side="bottom"
      align="end"
      width="sm"
      renderTrigger={({ onClick, open }) => (
        <IconButton
          icon="settings"
          variant="ghost"
          size="xs"
          title={t('project.sidebar.moreActions')}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${t('project.sidebar.moreActionsAria')} ${project.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        />
      )}
    >
      {({ close }) => (
        <div className="flex flex-col">
          <PopoverMenuItem
            icon="edit"
            label={t('project.sidebar.editProject')}
            onClick={() => {
              close();
              onEdit(project.id);
            }}
          />
          <PopoverMenuItem
            icon="plus"
            label={t('home.processSidebar.addSquad')}
            onClick={() => {
              close();
              onCreateSquad(project.id);
            }}
          />
        </div>
      )}
    </PopoverMenu>
  ) : undefined;

  return (
    <GroupedRail.Group
      title={project.name}
      isActive={project.isActive}
      count={project.openCount}
      trailing={trailing}
    >
      {project.squads.length === 0 && project.canEdit ? (
        <GroupedRail.Button
          title={t('home.processSidebar.addSquad')}
          icon="plus"
          onClick={() => {
            onCreateSquad(project.id);
          }}
        />
      ) : (
        project.squads.map((s) => (
          <GroupedRail.Item
            key={s.id}
            title={s.name}
            isActive={s.isActive}
            count={s.openCount}
            onClick={() => {
              onSelectSquad(s.id);
            }}
            trailing={
              s.canEdit ? (
                <IconButton
                  icon="settings"
                  variant="ghost"
                  size="xs"
                  title={t('home.processSidebar.configureSquad')}
                  aria-label={`${t('home.processSidebar.configureSquad')} ${s.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditSquad(s.id);
                  }}
                />
              ) : undefined
            }
          />
        ))
      )}
    </GroupedRail.Group>
  );
}

type PopoverMenuItemProps = {
  icon: 'edit' | 'plus';
  label: string;
  onClick: () => void;
};

function PopoverMenuItem({
  icon,
  label,
  onClick,
}: Readonly<PopoverMenuItemProps>): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-2 rounded-sm border-0 bg-transparent px-2.5 py-1.5 text-left text-meta text-foreground-default transition-colors duration-fast ease-standard hover:bg-primary-tint hover:text-primary-ink"
    >
      <AppIcons
        name={icon}
        size={14}
        className="text-muted-foreground transition-colors group-hover:text-primary"
      />
      <span className="flex-1">{label}</span>
    </button>
  );
}
